import { randImageUrl, randUpperAlnum, randBracketToken, randVideoUrl, embedUrlFor } from './random.js';
import { stripGeo } from './geoSanitiser.js';
import { jsonStringEscape } from './escaping.js';

const DEFAULT_VIDEO_HOST = 'https://lanetacampeche.com/video';

// A natural video name: "The Honest SEO NEO Breakdown" if the subtitle
// already names the product, otherwise "SEO NEO: <subtitle>". The long
// SEO-title phrase is never used here.
const videoName = (product, subtitle) => {
  const clean = String(subtitle || '').replace(/[()]/g, '').replace(/\s+/g, ' ').trim();
  if (!clean) return product;
  return clean.toLowerCase().includes(String(product).toLowerCase()) ? clean : `${product}: ${clean}`;
};

const makeUniqueDescription = (keyword, product, subtitle, cardContent, rng) => {
  const subtitleClean = subtitle.replace(/[()]/g, '').trim();
  const openers = [
    `This video covers ${product} — ${subtitleClean}.`,
    `An in-depth look at ${product}: ${subtitleClean}.`,
    `Everything you need to know about ${product} — ${subtitleClean}.`,
    `${product} reviewed from every angle: ${subtitleClean}.`,
    `Breaking down ${product} — ${subtitleClean}.`,
  ];
  const opener = openers[Math.floor(rng._next() * openers.length)];
  const base = cardContent
    ? cardContent.replace(/Try It Today|Order .+ Now|.+ Where to Buy|Try .+ Today/gi, '').trim()
    : `${product} is reviewed with full transparency in this video.`;
  return stripGeo(`${opener} ${base}`.slice(0, 300));
};

const buildHasPart = (chapters, durationSecs, canonical) => {
  const segLen = Math.floor(durationSecs / chapters.length);
  return chapters.map((name, i) => {
    const start = i * segLen;
    const end = i === chapters.length - 1 ? durationSecs : (i + 1) * segLen - 1;
    return { name: stripGeo(name), start, end, url: `${canonical}#t=${start}` };
  });
};

export const buildAllVideoSchemas = ({
  keyword, product, canonical, isoDate, host, cardContents, rng,
  customImageBase, customVideoBase,
  primarySubtitle, primaryDuration, primaryHasPart,
  secondaryVideoSubtitles, secondaryChapterSets,
}) => {
  const effectiveBase = customImageBase || null;
  const effectiveVideoBase = customVideoBase || null;

  // ── Primary video ──────────────────────────────────────────────────────────
  const primaryContentUrl = randVideoUrl(DEFAULT_VIDEO_HOST, rng, effectiveVideoBase);
  const primaryMins = Math.floor(primaryDuration / 60);
  const primarySecs = primaryDuration % 60;

  const primarySchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: stripGeo(videoName(product, primarySubtitle)),
    description: stripGeo(`${videoName(product, primarySubtitle)} — a full video review with chapters.`),
    thumbnailUrl: [randImageUrl(host, rng, effectiveBase)],
    uploadDate: `${isoDate}+00:00`,
    duration: `PT${primaryMins}M${primarySecs}S`,
    contentUrl: primaryContentUrl,
    // Video URL base + the same random video ID, without .mp4.
    embedUrl: embedUrlFor(primaryContentUrl),
    // Chapters only exist when the owner supplied real timestamps.
    ...(primaryHasPart.length ? { hasPart: primaryHasPart.map((p) => ({
      '@type': 'Clip',
      name: stripGeo(p.name),
      startOffset: p.start,
      endOffset: p.end,
      url: `${canonical}#t=${p.start}`,
    })) } : {}),
  };

  const primaryBlock = `<script type="application/ld+json">${JSON.stringify(primarySchema, null, 0)}</script>`;

  // ── Secondary videos ──────────────────────────────────────────────────────
  const secondaryBlocks = [];
  const count = Math.min(
    secondaryVideoSubtitles ? secondaryVideoSubtitles.length : 0,
    secondaryChapterSets ? secondaryChapterSets.length : 0,
    5
  );

  for (let i = 0; i < count; i++) {
    const uniqueSubtitle = stripGeo(secondaryVideoSubtitles[i]);
    const chapters = (secondaryChapterSets[i] || []).map(stripGeo);
    if (!uniqueSubtitle || chapters.length === 0) continue;

    const durSecs = 90 + Math.floor(rng._next() * 810);
    const durMins = Math.floor(durSecs / 60);
    const durRem = durSecs % 60;

    const cardIdx = i % Math.max(1, cardContents.length);
    const uniqueDesc = makeUniqueDescription(keyword, product, uniqueSubtitle, cardContents[cardIdx], rng);
    const contentUrl = randVideoUrl(DEFAULT_VIDEO_HOST, rng, effectiveVideoBase);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: stripGeo(videoName(product, uniqueSubtitle)),
      description: uniqueDesc,
      thumbnailUrl: [randImageUrl(host, rng, effectiveBase)],
      uploadDate: `${isoDate}+00:00`,
      duration: `PT${durMins}M${durRem}S`,
      contentUrl,
      embedUrl: embedUrlFor(contentUrl),
      // No hasPart: nobody knows what happens at which second of these
      // videos, so chapter timestamps would be invented.
    };

    secondaryBlocks.push(`<script type="application/ld+json">${JSON.stringify(schema, null, 0)}</script>`);
  }

  return { allBlocks: [primaryBlock, ...secondaryBlocks], totalCount: 1 + secondaryBlocks.length };
};

export const buildHydrationVideoSchemaStr = ({
  keyword, product, subtitle, canonical, isoDate, host,
  primaryHasPart, primaryDuration, rng,
  customImageBase, customVideoBase,
}) => {
  const effectiveBase = customImageBase || null;
  const effectiveVideoBase = customVideoBase || null;
  const contentUrl = randVideoUrl(DEFAULT_VIDEO_HOST, rng, effectiveVideoBase);
  const mins = Math.floor(primaryDuration / 60);
  const secs = primaryDuration % 60;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: stripGeo(videoName(product || keyword, subtitle)),
    description: stripGeo(videoName(product || keyword, subtitle)) + ' — a full video review with chapters.',
    thumbnailUrl: [randImageUrl(host, rng, effectiveBase)],
    uploadDate: `${isoDate}+00:00`,
    duration: `PT${mins}M${secs}S`,
    contentUrl,
    embedUrl: embedUrlFor(contentUrl),
    ...(primaryHasPart.length ? { hasPart: primaryHasPart.map((p) => ({
      '@type': 'Clip',
      name: stripGeo(p.name),
      startOffset: p.start,
      endOffset: p.end,
      url: p.url,
    })) } : {}),
  };

  return jsonStringEscape(JSON.stringify(schema, null, 0));
};